import React, { useCallback } from 'react';
import { getErrorMessage } from 'utils/errorUtils';
import { errorNotification } from 'utils/notifications';
import { stillSelectedDetail } from 'utils/uploadMessages';
import * as xlsx from 'xlsx';
import { Icon } from '@timeedit/ui-components';
import {
  type DraggerProps,
  Typography,
  Upload,
  type UploadFile,
} from '@timeedit/ui-components/antd';
import type { UserPreview } from './UserPreview';

const { Dragger } = Upload;

const ACCEPTED_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const MAX_FILE_SIZE_MB = 200;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1000 * 1000;

export type FileReadResult =
  | { uid: string; kind: 'parsed'; data: UserPreview[]; columns: string[] }
  | { uid: string; kind: 'failed'; message: string; details?: string[] };

export type OnFileRead = (result: FileReadResult) => void;

type Props = {
  onFileRead: OnFileRead;
  fileList: UploadFile[];
  onFileListChange: (fileList: UploadFile[]) => void;
};

export const UploadUser = ({
  onFileRead,
  fileList,
  onFileListChange,
}: Props) => {
  const readUploadFile = useCallback<NonNullable<DraggerProps['beforeUpload']>>(
    (file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        errorNotification(
          'Invalid file type. Please upload a CSV or Excel file.',
          stillSelectedDetail(fileList)
        );
        return Upload.LIST_IGNORE;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        errorNotification(
          `Max file size is ${MAX_FILE_SIZE_MB} MB`,
          stillSelectedDetail(fileList)
        );
        return Upload.LIST_IGNORE;
      }

      const isCsv = file.name?.toLowerCase().endsWith('.csv');
      const reader = new FileReader();

      reader.onerror = () =>
        onFileRead({
          uid: file.uid,
          kind: 'failed',
          message: 'Could not read the file',
          details: [
            'The browser failed to read the selected file. Please try again.',
          ],
        });

      reader.onload = (progressEvent) => {
        try {
          const data = progressEvent.target?.result;
          const workbook = xlsx.read(data, {
            type: isCsv ? 'string' : 'array',
            raw: true,
            cellDates: true,
          });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          if (!worksheet) {
            onFileRead({
              uid: file.uid,
              kind: 'failed',
              message: 'Could not read the spreadsheet',
              details: ['The file contains no worksheets.'],
            });
            return;
          }

          const rows = xlsx.utils.sheet_to_json(worksheet, { header: 1 });
          const [headerRow] = rows as Array<Array<unknown>>;
          const columns = (headerRow || [])
            .map((cell) => String(cell).trim())
            .filter(Boolean);

          const json = xlsx.utils.sheet_to_json<UserPreview>(worksheet);
          onFileRead({ uid: file.uid, kind: 'parsed', data: json, columns });
        } catch (error) {
          onFileRead({
            uid: file.uid,
            kind: 'failed',
            message: 'Could not read the spreadsheet',
            details: [getErrorMessage(error)],
          });
        }
      };

      if (isCsv) {
        reader.readAsText(file, 'UTF-8');
      } else {
        reader.readAsArrayBuffer(file);
      }

      return false;
    },
    [onFileRead, fileList]
  );

  return (
    <div style={{ marginTop: 50 }}>
      <Dragger
        name="file"
        multiple={false}
        maxCount={1}
        accept=".csv,.xls,.xlsx"
        fileList={fileList}
        onChange={(info) => onFileListChange(info.fileList)}
        beforeUpload={readUploadFile}
      >
        <div className="te-upload-container">
          <Icon
            id="CloudUploadOutlined"
            alt="Cloud Upload"
            className="icon-upload"
          />
          <Typography.Title>Choose file to Upload</Typography.Title>
          <Typography.Paragraph>
            Or drag and drop theme here
          </Typography.Paragraph>
        </div>
      </Dragger>
    </div>
  );
};
